import { Injectable } from '@angular/core';
import { LockPointEnum } from '../../enums/lockpoint';
import { BoundingBox } from '../../models/bounding-box';
import { BoundingPoint } from '../../models/bounding-point';

@Injectable({
    providedIn: 'root'
})
export class BoundingBoxService {
    //
    // RESTRICT METHODS
    //

    // CropBox has the structure [x, y, width, height] as an array
    public restrictCropBoxToOuterBounds(
        cropBox: number[],
        outerBounds: number[],
        lockPoint = -1,
        minWidth = -1,
        minHeight = -1,
        minDimension = -1
    ): number[] {
        let [cropX, cropY, cropWidth, cropHeight] = cropBox;

        const boundsArray = [
            cropX,
            cropY,
            cropX + cropWidth,
            cropY + cropHeight
        ];
        const restrictedBounds = this.restrictBoundsToOuterBounds(
            boundsArray,
            outerBounds,
            lockPoint,
            minWidth,
            minHeight,
            minDimension
        );

        [cropX, cropY] = restrictedBounds;
        cropWidth = restrictedBounds[2] - restrictedBounds[0];
        cropHeight = restrictedBounds[3] - restrictedBounds[1];

        return [cropX, cropY, cropWidth, cropHeight];
    }

    // BoundingBox has the structure [x1, y1, x2, y2] within the IBoundingBox object
    public restrictBoundingBoxToOuterBounds(
        boundingBox: BoundingBox,
        outerBounds: number[],
        lockPoint = -1,
        minWidth = -1,
        minHeight = -1,
        minDimension = -1
    ): BoundingBox {
        let x1 = boundingBox.min.x;
        let y1 = boundingBox.min.y;
        let x2 = boundingBox.max.x;
        let y2 = boundingBox.max.y;

        const boundsArray = [x1, y1, x2, y2];
        const restrictedBounds = this.restrictBoundsToOuterBounds(
            boundsArray,
            outerBounds,
            lockPoint,
            minWidth,
            minHeight,
            minDimension
        );

        [x1, y1, x2, y2] = restrictedBounds;

        const restrictedBoundingPointMin: BoundingPoint = { x: x1, y: y1 };
        const restrictedBoundingPointMax: BoundingPoint = { x: x2, y: y2 };
        const restrictedBoundingBox: BoundingBox = {
            min: restrictedBoundingPointMin,
            max: restrictedBoundingPointMax
        };
        return restrictedBoundingBox;
    }

    // Bounds has the structure [x1, y1, x2, y2] as an array
    public restrictBoundsToOuterBounds(
        bounds: number[],
        outerBounds: number[],
        lockPoint = -1,
        minWidth = -1,
        minHeight = -1,
        minDimension = -1
    ): number[] {
        const [minX, minY, maxX, maxY] = outerBounds;

        let lockX1 =
            lockPoint === LockPointEnum.topLeft ||
            lockPoint === LockPointEnum.bottomLeft;
        let lockX2 =
            lockPoint === LockPointEnum.topRight ||
            lockPoint === LockPointEnum.bottomRight;
        let lockY1 =
            lockPoint === LockPointEnum.topLeft ||
            lockPoint === LockPointEnum.topRight;
        let lockY2 =
            lockPoint === LockPointEnum.bottomLeft ||
            lockPoint === LockPointEnum.bottomRight;

        let [x1, y1, x2, y2] = bounds;
        const width = x2 - x1;
        const height = y2 - y1;

        if (minDimension > 0 && Math.max(width, height) < minDimension) {
            // The longest side is less than minDimension
            if (width > height) {
                // The longest side is the width
                [x1, x2, lockX1, lockX2] = this.lockDimension(
                    x1,
                    x2,
                    lockX1,
                    lockX2,
                    minWidth,
                    minDimension
                );
            } else {
                // The longest side is the height
                [y1, y2, lockY1, lockY2] = this.lockDimension(
                    y1,
                    y2,
                    lockY1,
                    lockY2,
                    minHeight,
                    minDimension
                );
            }
        }

        if (x2 > maxX) {
            [x1, x2] = this.restrictPointToBounds(
                x1,
                x2,
                lockX1,
                lockX2,
                x2 - maxX
            );
        }

        if (y2 > maxY) {
            [y1, y2] = this.restrictPointToBounds(
                y1,
                y2,
                lockY1,
                lockY2,
                y2 - maxY
            );
        }

        if (x1 < minX) {
            [x1, x2] = this.restrictPointToBounds(
                x1,
                x2,
                lockX1,
                lockX2,
                x1 - minX
            );
        }

        if (y1 < minY) {
            [y1, y2] = this.restrictPointToBounds(
                y1,
                y2,
                lockY1,
                lockY2,
                y1 - minY
            );
        }

        [x1, x2] = this.restrictDimension(minWidth, x1, x2, lockX1, lockX2);
        [y1, y2] = this.restrictDimension(minHeight, y1, y2, lockY1, lockY2);

        x1 = Math.max(0, x1);
        y1 = Math.max(0, y1);
        x2 = Math.min(maxX, x2);
        y2 = Math.min(maxY, y2);

        return [x1, y1, x2, y2];
    }

    private lockDimension(
        min: number,
        max: number,
        isMinLocked: boolean,
        isMaxLocked: boolean,
        lockedDimension: number,
        minDimensionSize: number
    ): [number, number, boolean, boolean] {
        if (isMaxLocked) {
            // The user was dragging from top-left or top-right
            min = max - Math.max(lockedDimension, minDimensionSize);
            isMinLocked = true;
        } else if (isMinLocked) {
            // The user was dragging from bottom-left or bottom-right
            max = min + Math.max(lockedDimension, minDimensionSize);
            isMaxLocked = true;
        }

        return [min, max, isMinLocked, isMaxLocked];
    }

    private restrictDimension(
        minDimension: number,
        min: number,
        max: number,
        isMinLocked: boolean,
        isMaxLocked: boolean
    ): Array<number> {
        if (minDimension > 0) {
            const dimension = max - min;
            if (dimension < minDimension) {
                if (!isMaxLocked) {
                    max = min + minDimension;
                } else if (!isMinLocked) {
                    min = max - minDimension;
                }
            }
        }

        return [min, max];
    }

    private restrictPointToBounds(
        minCoord: number,
        maxCoord: number,
        isMinLocked: boolean,
        isMaxLocked: boolean,
        diff: number
    ): Array<number> {
        // let diff = maxCoord - outerBoundCood;
        if (!isMinLocked) {
            minCoord = minCoord - diff;
        }
        if (!isMaxLocked) {
            maxCoord = maxCoord - diff;
        }

        return [minCoord, maxCoord];
    }

    //
    // COMPARE METHODS
    //

    // CropBox has the structure [x, y, width, height] as an array
    public compareCropBoxes(cropBox1: number[], cropBox2: number[]): boolean {
        return this.compareBounds(cropBox1, cropBox2);
    }

    // BoundingBox has the structure [x1, y1, x2, y2] within the IBoundingBox object
    public areBoundingBoxesEqual(
        boundingBox1: BoundingBox,
        boundingBox2: BoundingBox
    ): boolean {
        if (!boundingBox1 && !boundingBox2) {
            return true;
        }

        if (!boundingBox1 || !boundingBox2) {
            return false;
        }

        if (boundingBox1.min.x !== boundingBox2.min.x) {
            return false;
        }

        if (boundingBox1.min.y !== boundingBox2.min.y) {
            return false;
        }

        if (boundingBox1.max.x !== boundingBox2.max.x) {
            return false;
        }

        if (boundingBox1.max.y !== boundingBox2.max.y) {
            return false;
        }

        return true;
    }

    // Bounds has the structure [x1, y1, x2, y2] as an array
    public compareBounds(bounds1: number[], bounds2: number[]): boolean {
        if (!bounds1 && !bounds2) {
            return true;
        }

        if (!bounds1 || !bounds2) {
            return false;
        }

        if (bounds1[0] !== bounds2[0]) {
            return false;
        }

        if (bounds1[1] !== bounds2[1]) {
            return false;
        }

        if (bounds1[2] !== bounds2[2]) {
            return false;
        }

        if (bounds1[3] !== bounds2[3]) {
            return false;
        }

        return true;
    }
}
